import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { RefreshCw, BatteryWarning, BatteryFull, BatteryMedium } from "lucide-react";
import { API } from "../../../config";

const fmt = (n) => (typeof n === "number" ? n.toLocaleString("it-IT") : "—");

/** iter184 — ElevenLabs character counter vs. the current episode's need (same pre-flight as autopilot step 7). */
export function TtsQuotaLight({ headers, dm, c }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await axios.get(`${API}/admin/podcast/tts-quota`, { headers });
      setState({ loading: false, data, error: null });
    } catch (e) {
      const d = e.response?.data;
      const det = d?.detail ?? d;  // the API flattens dict details to the top level
      setState({ loading: false, data: null, error: (typeof det === "string" ? det : det?.error) || e.message });
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const { loading, data, error } = state;
  const pct = data?.limit ? Math.round((data.remaining / data.limit) * 100) : null;
  const ok = data ? (data.ok === null ? data.remaining > 0 : data.ok) : false;
  const tone = error ? { bg: dm ? "rgba(220,38,38,0.12)" : "#fee2e2", fg: dm ? "#fca5a5" : "#7f1d1d" }
    : !data ? { bg: "transparent", fg: "inherit" }
    : ok ? { bg: dm ? "rgba(16,185,129,0.12)" : "#ecfdf5", fg: dm ? "#6ee7b7" : "#065f46" }
    : { bg: dm ? "rgba(249,115,22,0.14)" : "#ffedd5", fg: dm ? "#fdba74" : "#9a3412" };
  const Icon = error || (data && !ok) ? BatteryWarning : pct !== null && pct < 25 ? BatteryMedium : BatteryFull;

  return (
    <div data-testid="tts-quota-light" className="mt-3 rounded-lg px-3 py-2 text-[11px] flex items-start gap-2" style={{ background: tone.bg, color: tone.fg }}>
      <Icon className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="font-bold uppercase tracking-wider text-[9px]">Quota ElevenLabs</span>
          <button onClick={load} disabled={loading} data-testid="tts-quota-refresh" title="Ricontrolla" className="p-0.5 rounded hover:opacity-70">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
        {error && (
          <div data-testid="tts-quota-error">
            Contatore non leggibile: {error}
            {/user_read/i.test(error) && <> — abilita <strong>User → Read</strong> sulla chiave nel pannello ElevenLabs.</>}
          </div>
        )}
        {data && (
          <div data-testid="tts-quota-values">
            <strong>{fmt(data.remaining)}</strong> caratteri residui su {fmt(data.limit)} ({pct}%) · piano {data.tier || "—"}
            {data.renews_at_rome && <> · rinnovo {data.renews_at_rome}</>}
            {data.episode ? (
              <div className="mt-0.5 opacity-90" data-testid="tts-quota-episode">
                Episodio {data.episode.week_id}: servono <strong>{fmt(data.episode.needed)}</strong>
                {" "}(script {fmt(data.episode.script_chars)}{data.episode.cached_chars ? ` − cache ${fmt(data.episode.cached_chars)}` : ""} +10%)
                {" · "}{data.episode.ok ? "copertura OK" : "NON coperto: lo step 7 fallirà con tts_quota_insufficient"}
                {data.episode.audio_step_status && <> · step 7: {data.episode.audio_step_status}</>}
              </div>
            ) : <div className="mt-0.5 opacity-80">Nessun episodio autopilot corrente da confrontare.</div>}
          </div>
        )}
        {!data && !error && loading && <div>Controllo in corso…</div>}
      </div>
    </div>
  );
}
